import React, {
  ChangeEvent,
  FunctionComponent,
  KeyboardEvent as ReactKeyboardEvent,
  memo,
  useCallback,
  useEffect,
  useState,
} from 'react';
import './EditStoreConfigModal.scss';
import Fetch from '../../../../../utils/fetch';
import { settings } from '../../../../../settings';
import { DayOfWeek } from '../../../../../types/javaTypes';
import { capitalize } from '../../../../../utils/strings';
import { ButtonComponent } from '../../../../../ReusableComponents/Button/ButtonComponent';
import { CheckboxComponent } from '../../../../../ReusableComponents/Checkbox/CheckboxComponent';
import { InputComponent } from '../../../../../ReusableComponents/Input/InputComponent';
import { DropdownComponent } from '../../../../../ReusableComponents/Dropdown/DropdownComponent';
import { Loader } from '../../../../../components/Loader/Loader';
import { FaAngleDown, FaAngleUp } from 'react-icons/fa';
import TableContainer from '@material-ui/core/TableContainer';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import TableBody from '@material-ui/core/TableBody';
import IconButton from '@material-ui/core/IconButton';
import { FaTimes } from 'react-icons/fa';
import dayjs, { Dayjs } from 'dayjs';
import Flatpickr from 'react-flatpickr';
import clsx from 'clsx';

interface UpdateResult {
  success: boolean;
  errorMessage: string;
}

type DailyCapacity = Record<DayOfWeek, string | number | null>;
type CutOffTimePerDay = Record<DayOfWeek, string | null>;

const initValueForMaxOrdersPerDay: DailyCapacity = {
  [DayOfWeek.Monday]: '',
  [DayOfWeek.Tuesday]: '',
  [DayOfWeek.Wednesday]: '',
  [DayOfWeek.Thursday]: '',
  [DayOfWeek.Friday]: '',
  [DayOfWeek.Saturday]: '',
  [DayOfWeek.Sunday]: '',
};

const initValueForCutOffTimePerDay: CutOffTimePerDay = {
  [DayOfWeek.Monday]: '',
  [DayOfWeek.Tuesday]: '',
  [DayOfWeek.Wednesday]: '',
  [DayOfWeek.Thursday]: '',
  [DayOfWeek.Friday]: '',
  [DayOfWeek.Saturday]: '',
  [DayOfWeek.Sunday]: '',
};

const faAngle = (showMore, onShowMore) => {
  return (
    <>
      {showMore ? (
        <FaAngleDown
          style={{
            width: 30,
            height: 30,
            color: '#3a3d4b',
            cursor: 'pointer',
          }}
          className="desktop-only"
          onClick={() => onShowMore()}
        />
      ) : (
        <FaAngleUp
          style={{
            width: 30,
            height: 30,
            color: '#3a3d4b',
            cursor: 'pointer',
          }}
          className="desktop-only"
          onClick={() => onShowMore()}
        />
      )}
    </>
  );
};

type ErrorFields = {
  defaultMaxDailyOrders: boolean;
  defaultMaxDailyLargeOrders: boolean;
  dailyCutOffTimeState: boolean;
} & {
  [key in `${DayOfWeek}-${'cutOffTimePerDay' | 'maxOrdersPerDay'}`]?: boolean;
};

type HolidayCapacityDay = {
  accountId: number;
  date: string;
  product: string;
  maxOrdersPerDay: string;
  cutoff?: string;
};

interface Props {
  storeName: string;
  accountId: number;
  dailyCutOffTime?: string;
  pickupDays: DayOfWeek[];
  onCloseModal: () => void;
  showLarge: boolean;
  showDelivery: boolean;
  showSpecial: boolean;
  onShowLarge: () => void;
  onShowDelivery: () => void;
  onShowSpecial: () => void;
  toggleToastMessage: (isToastVisible: boolean) => void;
  setToastConfigObject: (configObject: {
    text: string;
    type: 'success' | 'error';
  }) => void;
  toggleLoader: (isVisible: boolean) => void;
  updateStoresCapacity: () => void;
}

const displayedWeekDays: DayOfWeek[] = [
  DayOfWeek.Monday,
  DayOfWeek.Tuesday,
  DayOfWeek.Wednesday,
  DayOfWeek.Thursday,
  DayOfWeek.Friday,
  DayOfWeek.Saturday,
  DayOfWeek.Sunday,
];

const errorFieldsObject: ErrorFields = {
  defaultMaxDailyOrders: false,
  defaultMaxDailyLargeOrders: false,
  dailyCutOffTimeState: false,
};

enum Product {
  Delivery = 'DELIVERY',
  Large = 'LARGE',
}

export const EditStoreConfigModal: FunctionComponent<Props> = memo(props => {
  const {
    storeName,
    accountId,
    onCloseModal,
    showLarge,
    showDelivery,
    showSpecial,
    onShowLarge,
    onShowDelivery,
    onShowSpecial,
    setToastConfigObject,
    toggleToastMessage,
    toggleLoader,
    updateStoresCapacity,
  } = props;

  const [inputErrorFields, setInputErrorFields] =
    useState<ErrorFields>(errorFieldsObject);
  const [isLoadingData, setIsLoadingData] = useState<boolean>(true);
  const [pickupDaysState, setPickupDaysState] = useState<DayOfWeek[]>([]);
  const [maxOrdersPerDay, setMaxOrdersPerDay] = useState<DailyCapacity>(
    initValueForMaxOrdersPerDay
  );
  const [defaultMaxDailyOrders, setDefaultMaxDailyOrders] =
    useState<string>('');
  const [defaultMaxDailyLargeOrders, setDefaultMaxDailyLargeOrders] =
    useState<string>('');
  const [dailyCutOffTime, setDailyCutOffTime] = useState<string | undefined>(
    ''
  );
  const [cutOffTimePerDay, setCutOffTimePerDay] = useState<CutOffTimePerDay>(
    initValueForCutOffTimePerDay
  );
  const [currentHolidayCapacity, setCurrentHolidayCapacity] = useState<any[]>(
    []
  );
  const [addedDays, setAddedDays] = useState<HolidayCapacityDay[]>([]);

  const getConfigToEdit = useCallback(async (): Promise<void> => {
    try {
      const response = await Fetch.get({
        url: `${settings.newApiUrl}/store/settings/internal/capacity?accountId=${accountId}`,
      });
      if (response.ok) {
        const result = await response.json();
        result.maxOrdersPerDay && setMaxOrdersPerDay(result.maxOrdersPerDay);
        result.defaultMaxDailyOrders &&
          setDefaultMaxDailyOrders(result.defaultMaxDailyOrders.toString());
        result.defaultMaxDailyLargeOrders &&
          setDefaultMaxDailyLargeOrders(
            result.defaultMaxDailyLargeOrders.toString()
          );
        result.dailyCutOffTime && setDailyCutOffTime(result.dailyCutOffTime);
        result.cutOffTimePerDay && setCutOffTimePerDay(result.cutOffTimePerDay);
        result.pickupDays && setPickupDaysState(result.pickupDays);
      }

      const holidayCapacityRespone = await Fetch.get({
        url: `${settings.newApiUrl}/capacity/holiday?accountId=${accountId}`,
      });
      if (holidayCapacityRespone.ok) {
        const result = await holidayCapacityRespone.json();
        if (result.holidayCapacities) {
          result.holidayCapacities = result.holidayCapacities.map(day => ({
            ...day,
            remove: false,
          }));
          setCurrentHolidayCapacity(result.holidayCapacities);
        }

        console.log(currentHolidayCapacity);
      }
      //TODO: Show error to enduser
    } catch (e) {
      console.error('Error fetching config to edit', e);
    }
    setIsLoadingData(false);
  }, [accountId]);

  const onChangePickupDay = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      const { checked, value } = event.target;
      if (checked && !pickupDaysState.includes(value as DayOfWeek)) {
        setPickupDaysState(currentPickupDaysState => [
          ...currentPickupDaysState,
          value as DayOfWeek,
        ]);
      } else {
        setPickupDaysState(currentPickupDaysState =>
          currentPickupDaysState.filter(day => day !== (value as DayOfWeek))
        );
      }
    },
    [pickupDaysState]
  );

  const escFunction = useCallback(
    (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onCloseModal();
      }
    },
    [onCloseModal]
  );

  const setOrClearErrorFields = useCallback(
    (field: string, value: string): void => {
      let isValid = _ => true;
      if (field === 'defaultMaxDailyOrders') {
        isValid = s => /^\d+$/.test(s) && s.trim() !== '';
      } else if (field === 'dailyCutOffTimeState') {
        isValid = s => /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(s);
      } else if (field === 'defaultMaxDailyLargeOrders') {
        isValid = s => /^\d+$/.test(s) && s.trim() !== '';
      } else if (field.endsWith('-cutOffTimePerDay')) {
        isValid = s =>
          /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(s) || s.trim() === '';
      } else if (field.endsWith('-maxOrdersPerDay')) {
        isValid = s => /^\d+$/.test(s) || s.trim() === '';
      }

      setInputErrorFields(currentErrorFields => ({
        ...currentErrorFields,
        [field]: !isValid(value),
      }));
    },
    []
  );

  const onChangeDefaultMaxDailyOrders = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      const { value } = event.target;
      setOrClearErrorFields('defaultMaxDailyOrders', value);
      setDefaultMaxDailyOrders(value);
    },
    [setOrClearErrorFields]
  );

  const onChangeDefaultMaxDailyLargeOrders = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      const { value } = event.target;
      setOrClearErrorFields('defaultMaxDailyLargeOrders', value);
      setDefaultMaxDailyLargeOrders(value);
    },
    [setOrClearErrorFields]
  );

  const onChangeMaxOrdersPerDay = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      const { value, name } = event.target;
      setOrClearErrorFields(name, value);
      const [day] = name.split('-');
      setMaxOrdersPerDay(currentMaxOrdersPerDay => ({
        ...currentMaxOrdersPerDay,
        [day]: value,
      }));
    },
    [setOrClearErrorFields]
  );

  const onChangeCutOffTimePerDay = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      const { value, name } = event.target;
      setOrClearErrorFields(name, value);
      const [day] = name.split('-');
      setCutOffTimePerDay(currentMaxOrdersPerDay => ({
        ...currentMaxOrdersPerDay,
        [day]: value,
      }));
    },
    [setOrClearErrorFields]
  );

  const onChangeDailyCutOff = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      const { value } = event.target;
      setOrClearErrorFields('dailyCutOffTimeState', value);
      setDailyCutOffTime(value);
    },
    [setOrClearErrorFields]
  );

  const onChangeHolidayCapacityProduct = useCallback(
    (
      event: ChangeEvent<{ name?: string; value: unknown }>,
      index: number
    ): void => {
      const updatedAddedDays = [...addedDays];
      updatedAddedDays[index].product = `${event.target.value}`;
      setAddedDays(updatedAddedDays);
    },
    [addedDays, setAddedDays]
  );

  const onChangeHolidayCapacityLimit = useCallback(
    (event: ChangeEvent<HTMLInputElement>, index: number): void => {
      const { value } = event.target;
      const updatedAddedDays = [...addedDays];
      updatedAddedDays[index].maxOrdersPerDay = value;
      setAddedDays(updatedAddedDays);
    },
    [addedDays, setAddedDays]
  );

  const onChangeHolidayCapacityCutoff = useCallback(
    (event: ChangeEvent<HTMLInputElement>, index: number): void => {
      const { value } = event.target;
      const updatedAddedDays = [...addedDays];
      updatedAddedDays[index].cutoff = value;
      setAddedDays(updatedAddedDays);
    },
    [addedDays, setAddedDays]
  );

  const onToggleRemoveHolidayCapacityDay = useCallback(
    (_, day) => {
      const current = [...currentHolidayCapacity];
      current.forEach((currentDay, i) => {
        if (currentDay.product == day.product && currentDay.date == day.date) {
          current[i].remove =
            current[i].remove == undefined ? true : !current[i].remove;
        }
      });
      setCurrentHolidayCapacity(current);
    },
    [currentHolidayCapacity, setCurrentHolidayCapacity]
  );

  const updateStoreSettings = useCallback(async (): Promise<UpdateResult> => {
    try {
      const res = await Fetch.put({
        url: `${settings.newApiUrl}/store/settings/internal/capacity`,
        body: {
          accountId,
          maxOrdersPerDay,
          defaultMaxDailyOrders: +defaultMaxDailyOrders || null,
          defaultMaxDailyLargeOrders: defaultMaxDailyLargeOrders,
          dailyCutOffTime: dailyCutOffTime,
          cutOffTimePerDay,
          pickupDays: pickupDaysState,
        },
      });
      if (res.status === 422) {
        return {
          success: false,
          errorMessage: (await res.json()).map(
            (msg: { message: string }) => msg.message
          ),
        };
      } else if (!res.ok) {
        return {
          success: false,
          errorMessage: 'The store settings could not be updated',
        };
      }

      if (addedDays.length > 0) {
        const saveHolidayCapacityResult = await Fetch.put({
          url: `${settings.newApiUrl}/capacity/holiday`,
          body: {
            accountId,
            holidayCapacities: addedDays,
          },
        });

        if (!saveHolidayCapacityResult.ok) {
          return {
            success: false,
            errorMessage: 'Holiday capacities for store could not be updated',
          };
        }
      }

      const holidayCapacityToRemove = currentHolidayCapacity.filter(
        day => day.remove
      );

      if (holidayCapacityToRemove.length > 0) {
        const saveHolidayCapacityResult = await Fetch.delete({
          url: `${settings.newApiUrl}/capacity/holiday`,
          body: {
            accountId,
            holidayCapacities: holidayCapacityToRemove,
          },
        });

        if (!saveHolidayCapacityResult.ok) {
          return {
            success: false,
            errorMessage: 'Holiday capacities for store could not be deleted',
          };
        }
      }

      return { success: true, errorMessage: '' };
    } catch (e) {
      console.error('Error updating store settings', e);
      return {
        success: false,
        errorMessage: 'The store settings could not be updated',
      };
    }
  }, [
    accountId,
    dailyCutOffTime,
    cutOffTimePerDay,
    defaultMaxDailyOrders,
    defaultMaxDailyLargeOrders,
    maxOrdersPerDay,
    pickupDaysState,
    addedDays,
    currentHolidayCapacity,
  ]);

  const saveSettings = useCallback(async (): Promise<void> => {
    const result = await updateStoreSettings();
    if (result.success) {
      setToastConfigObject({
        text: `New configuration for ${capitalize(storeName)} was set stored`,
        type: 'success',
      });
      toggleLoader(true);
      toggleToastMessage(true);
      updateStoresCapacity();
      onCloseModal();
    } else {
      setToastConfigObject({ text: result.errorMessage, type: 'error' });
      toggleToastMessage(true);
    }
  }, [
    onCloseModal,
    updateStoreSettings,
    setToastConfigObject,
    storeName,
    toggleLoader,
    toggleToastMessage,
    updateStoresCapacity,
  ]);

  const enterKeyPressed = useCallback(
    async (event: ReactKeyboardEvent<HTMLDivElement>): Promise<void> => {
      if (
        event.key === 'Enter' &&
        !Object.keys(inputErrorFields).some(
          key => inputErrorFields[key as keyof ErrorFields]
        )
      ) {
        await saveSettings();
      }
    },
    [inputErrorFields, saveSettings]
  );
  useEffect(() => {
    getConfigToEdit()
      .then(() => console.log('fetched data'))
      .catch(error => console.log(error));
    document.addEventListener('keydown', escFunction, false);
    return () => {
      document.removeEventListener('keydown', escFunction, false);
    };
  }, [escFunction, getConfigToEdit]);

  const addDates = (selectedDates: Date[], _, instance) => {
    if (selectedDates.length == 0) {
      return;
    }

    const start = dayjs(selectedDates[0]);
    const end = dayjs(selectedDates[selectedDates.length - 1]);
    const numDays = Math.abs(end.diff(start, 'day')) + 1;
    const newDays = [];
    let n = 0;
    while (n < numDays) {
      const newDay: HolidayCapacityDay = {
        accountId,
        date: dayjs(start).add(n, 'day').format('YYYY-MM-DD'),
        product: Product.Delivery,
        maxOrdersPerDay: '',
        cutoff: undefined,
      };
      newDays.push(newDay);
      n++;
    }
    setAddedDays(newDays);
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: '350px 1fr',
    alignItems: 'center',
    paddingBottom: '1em',
  };
  const toRemoveStyle = {
    backgroundColor: '#FFBBBB',
    color: '#CC0000',
  };
  const end = addedDays.length > 0 ? addedDays.length - 1 : 0;
  const date = 'today';
  const format = 'YYYY-MM-DD';

  const SpecialCapacityDatePicker = ({ buttonText }) => {
    return (
      <div className="special-capacity-datepicker">
        <Flatpickr
          onClose={addDates}
          className=""
          options={{
            minDate: 'today',
            maxDate: null,
            disableMobile: true,
            dateFormat: 'y-m-d',
            showMonths: 1,
            shorthandCurrentMonth: true,
            locale: { firstDayOfWeek: 1 },
            mode: 'range',
          }}
          value={dayjs(date).toDate()}
          render={(_, ref) => (
            <div
              className={clsx(
                'calendar-button-wrapper',
                'SpecialCapacityCalendar'
              )}
              ref={ref}
            >
              <ButtonComponent text={buttonText} />
            </div>
          )}
        />
      </div>
    );
  };

  const availableProductSelection = [
    {
      name: 'Delivery',
      value: 'DELIVERY',
    },
  ];
  if (Number(defaultMaxDailyLargeOrders) > 0) {
    availableProductSelection.push({
      name: 'Large',
      value: 'LARGE',
    });
  }

  return (
    <div className="edit-store-config-modal">
      {isLoadingData ? (
        <Loader size="xxl" type="spinner" />
      ) : (
        <div className="edit-store-config-modal__content">
          <div className="edit-store-header">
            <span>Delivery</span>
            {faAngle(showDelivery, onShowDelivery)}
          </div>
          {showDelivery && (
            <div>
              <div className="edit-store-config-modal__content-row">
                <span>Maximum orders per day:</span>
                <InputComponent
                  variant="outlined"
                  data-lpignore="true"
                  label=""
                  size="small"
                  error={inputErrorFields.defaultMaxDailyOrders}
                  id="default-max-daily-orders"
                  name="default-max-daily-orders"
                  value={defaultMaxDailyOrders || ''}
                  onChange={onChangeDefaultMaxDailyOrders}
                  onKeyPress={enterKeyPressed}
                />
              </div>
              <div className="edit-store-config-modal__content-row">
                <span>Daily cut off time:</span>
                <span /> {/* Skip column to align this input with the others */}
                <InputComponent
                  variant="outlined"
                  data-lpignore="true"
                  label=""
                  size="small"
                  id="daily-cut-off-time"
                  name="daily-cut-off-time"
                  value={dailyCutOffTime || ''}
                  error={inputErrorFields.dailyCutOffTimeState}
                  placeholder="00:00"
                  onChange={onChangeDailyCutOff}
                  onKeyPress={enterKeyPressed}
                />
              </div>
              {displayedWeekDays.map(day => (
                <div
                  className="edit-store-config-modal__content-row"
                  key={`${day.toLowerCase()}MaxCapacity`}
                >
                  <CheckboxComponent
                    checked={pickupDaysState.includes(day)}
                    value={day}
                    handleChange={onChangePickupDay}
                    name="checkbox-day-capacity"
                    label={capitalize(day)}
                    labelPlacement="end"
                  />
                  <InputComponent
                    className={'edit-store-config-modal__input'}
                    data-lpignore="true"
                    data-day={day}
                    variant="outlined"
                    id={`${day.toLowerCase()}MaxCapacity`}
                    label=""
                    placeholder={defaultMaxDailyOrders || ''}
                    error={inputErrorFields[`${day}-maxOrdersPerDay`]}
                    disabled={!pickupDaysState.includes(day)}
                    name={`${day}-maxOrdersPerDay`}
                    value={maxOrdersPerDay[day] ?? ''}
                    size="small"
                    onChange={onChangeMaxOrdersPerDay}
                    onKeyPress={enterKeyPressed}
                  />
                  <InputComponent
                    className={'edit-store-config-modal__input'}
                    data-lpignore="true"
                    data-day={day}
                    variant="outlined"
                    id={`${day.toLowerCase()}CutOffTime`}
                    label=""
                    error={inputErrorFields[`${day}-cutOffTimePerDay`]}
                    placeholder={dailyCutOffTime || ''}
                    disabled={!pickupDaysState.includes(day)}
                    name={`${day}-cutOffTimePerDay`}
                    value={cutOffTimePerDay[day] || ''}
                    size="small"
                    onChange={onChangeCutOffTimePerDay}
                    onKeyPress={enterKeyPressed}
                  />
                </div>
              ))}
            </div>
          )}
          <div className="edit-store-header">
            <span>Large</span>
            {faAngle(showLarge, onShowLarge)}
          </div>
          {showLarge && (
            <div className="edit-store-config-modal__content-row">
              <span>Maximum large orders per day:</span>
              <InputComponent
                variant="outlined"
                data-lpignore="true"
                label=""
                size="small"
                error={inputErrorFields.defaultMaxDailyLargeOrders}
                id="default-max-daily-orders"
                name="default-max-daily-orders"
                value={defaultMaxDailyLargeOrders || ''}
                onChange={onChangeDefaultMaxDailyLargeOrders}
                onKeyPress={enterKeyPressed}
              />
            </div>
          )}
          <div className="edit-store-header">
            <span>Special capacity</span>
            {faAngle(showSpecial, onShowSpecial)}
          </div>
          {showSpecial && (
            <div
              className="special-capacity-container"
              style={{ marginTop: '1em' }}
            >
              {addedDays.length > 0 ? (
                <div style={gridStyle}>
                  <span style={{ fontWeight: 500 }}>
                    Chosen date{addedDays.length == 1 ? '' : 's'}:{'  '}
                    {addedDays.length == 1 ? (
                      <span style={{ fontWeight: 600 }}>
                        {dayjs(addedDays[0].date).format(format)}
                      </span>
                    ) : (
                      <span style={{ fontWeight: 600 }}>
                        {dayjs(addedDays[0].date).format(format)}
                        {` to ${dayjs(addedDays[end].date).format(format)}`}
                      </span>
                    )}
                  </span>
                  <span style={{ justifySelf: 'end' }}>
                    <SpecialCapacityDatePicker buttonText="Change" />
                  </span>
                </div>
              ) : (
                <div className="edit-store-config-modal__content-row">
                  <SpecialCapacityDatePicker buttonText="Add special capacity dates" />
                </div>
              )}
              {addedDays.length > 0 && (
                <TableContainer style={{ marginBottom: '1em' }}>
                  <Table padding="none">
                    <TableHead>
                      <TableRow>
                        <TableCell></TableCell>
                        <TableCell>Product:</TableCell>
                        <TableCell>Limit:</TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {addedDays.map((day: HolidayCapacityDay, i) => (
                        <TableRow
                          key={`addedDayRow_${dayjs(day.date).format(format)}`}
                        >
                          <TableCell style={{ fontWeight: 700 }}>
                            {dayjs(day.date).format(format)}
                          </TableCell>
                          <TableCell>
                            <DropdownComponent
                              formClassName="specialCapacityDropdown"
                              id={`addedDay_${i}_product`}
                              labelId={`addedDay_${i}_product`}
                              label=""
                              value={day.product.toUpperCase()}
                              itemsToSelect={availableProductSelection}
                              onChange={event => {
                                onChangeHolidayCapacityProduct(event, i);
                              }}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <InputComponent
                              variant="outlined"
                              data-lpignore="true"
                              label=""
                              size="small"
                              id={`addedDay_${i}_limit`}
                              name={`addedDay_${i}_limit`}
                              value={day.maxOrdersPerDay || ''}
                              placeholder="100"
                              onChange={event => {
                                onChangeHolidayCapacityLimit(event, i);
                              }}
                              onKeyPress={enterKeyPressed}
                            />
                          </TableCell>
                          <TableCell>
                            <IconButton
                              aria-label="remove"
                              onClick={e => {
                                setAddedDays(
                                  addedDays.filter((_, index) => index !== i)
                                );
                              }}
                            >
                              <FaTimes size={10} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              {currentHolidayCapacity?.length > 0 && (
                <div>
                  <div style={{ fontWeight: 600 }}>
                    Special capacity already registered:
                  </div>
                  <TableContainer>
                    <Table padding="none">
                      <TableHead>
                        <TableRow>
                          {['Date', 'Product', 'Limit'].map((title, i) => (
                            <TableCell
                              style={{ fontWeight: 600 }}
                              key={`headers_${i}`}
                            >
                              {title}:
                            </TableCell>
                          ))}
                          <TableCell />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {currentHolidayCapacity.map((day, i) => (
                          <TableRow
                            key={`capacity${dayjs(day.date).format(format)}`}
                            style={day.remove ? toRemoveStyle : null}
                          >
                            <TableCell
                              style={day.remove ? toRemoveStyle : null}
                            >
                              {dayjs(day.date).format(format)}
                            </TableCell>
                            <TableCell
                              style={day.remove ? toRemoveStyle : null}
                            >
                              {capitalize(day.product)}
                            </TableCell>
                            <TableCell
                              style={day.remove ? toRemoveStyle : null}
                            >
                              {day.maxOrdersPerDay}
                            </TableCell>
                            <TableCell>
                              <IconButton
                                aria-label="remove"
                                onClick={e => {
                                  onToggleRemoveHolidayCapacityDay(e, day);
                                }}
                              >
                                <FaTimes size={10} />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="edit-store-config-modal__button-section">
        <ButtonComponent
          text="Cancel"
          variant="outlined"
          onClick={onCloseModal}
        />
        <ButtonComponent
          text="Save"
          onClick={saveSettings}
          disabled={Object.keys(inputErrorFields).some(
            key => inputErrorFields[key as keyof ErrorFields]
          )}
        />
      </div>
    </div>
  );
});

EditStoreConfigModal.displayName = 'EditStoreConfigModal';
EditStoreConfigModal.whyDidYouRender = true;
